import { gql } from '@apollo/client';
import { CHAT_PARTS_DEFAULT, CHAT_PARTS_MAX } from '@arthelper/shared/chat-parts';

import { POST_AUTHOR_FIELDS } from './queries';

export const SIGN_UP_MUTATION = gql`
  mutation SignUp($email: String!, $password: String!) {
    signup(email: $email, password: $password)
  }
`;

export const SIGN_IN_MUTATION = gql`
  mutation SignIn($email: String!, $password: String!) {
    signin(email: $email, password: $password)
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      asfId
      firstName
      lastName
      story
      brandVoice
      voiceFormalities {
        id
      }
      voiceTones {
        id
      }
      voiceAvoidWords {
        id
        words
      }
      isDefaultArtworkPublic
    }
  }
`;

export const UPDATE_PROFILE = gql`
  mutation UpdateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      id
      userId
      avatarUrl
      bio
      displayName
      artistStatement
      countryCode
      country
      regionCode
      region
      city
      latitude
      longitude
      timezone
      locale
      autoTranslateEnabled
      autoTranslateExcludedLocales
      bannerUrl
      slug
      socialLinks
      isPublic
      watermarkEnabled
      watermarkUrl
      showMockups
      showArtworkSpecs
      showActivity
      isAnnounceNewArtwork
      categories {
        id
        name
        slug
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_IMAGE_ORDER = gql`
  mutation UpdateImageOrder($input: UpdateImageOrderInput!) {
    updateImageOrder(input: $input) {
      id
      imageOrder
    }
  }
`;

export const MARK_MOCKUP_DOWNLOADED = gql`
  mutation MarkMockupDownloaded($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      id
      hasMockupDownloads
    }
  }
`;

export const UPLOAD_PROFILE_IMAGE = gql`
  mutation UploadProfileImage($type: ProfileImageType!, $contentType: String) {
    uploadProfileImage(type: $type, contentType: $contentType) {
      success
      url
      key
      error
    }
  }
`;

export const ADMIN_UPLOAD_PROFILE_IMAGE = gql`
  mutation AdminUploadProfileImage($userId: ID!, $type: ProfileImageType!, $contentType: String) {
    adminUploadProfileImage(userId: $userId, type: $type, contentType: $contentType) {
      success
      url
      key
      error
    }
  }
`;

export const PROCESS_PROFILE_IMAGE = gql`
  mutation ProcessProfileImage($key: String!, $type: ProfileImageType!) {
    processProfileImage(key: $key, type: $type) {
      success
      url
      error
    }
  }
`;

export const ADMIN_PROCESS_PROFILE_IMAGE = gql`
  mutation AdminProcessProfileImage($userId: ID!, $key: String!, $type: ProfileImageType!) {
    adminProcessProfileImage(userId: $userId, key: $key, type: $type) {
      success
      url
      error
    }
  }
`;

export const UPLOAD_WATERMARK = gql`
  mutation UploadWatermark {
    uploadWatermark {
      url
      key
    }
  }
`;

export const REMOVE_WATERMARK = gql`
  mutation RemoveWatermark {
    removeWatermark
  }
`;

export const ADMIN_UPLOAD_WATERMARK = gql`
  mutation AdminUploadWatermark($userId: ID!) {
    adminUploadWatermark(userId: $userId) {
      url
      key
    }
  }
`;

export const ADMIN_REMOVE_WATERMARK = gql`
  mutation AdminRemoveWatermark($userId: ID!) {
    adminRemoveWatermark(userId: $userId)
  }
`;

export const ADMIN_UPDATE_USER = gql`
  mutation AdminUpdateUser($input: UpdateAdminUserInput!) {
    adminUpdateUser(input: $input) {
      asfId
      firstName
      lastName
      story
      brandVoice
    }
  }
`;

export const ADMIN_DELETE_USER = gql`
  mutation AdminDeleteUser($asfId: ID!) {
    adminDeleteUser(asfId: $asfId)
  }
`;

export const UPDATE_IMAGE_MUTATION = gql`
  mutation UpdateImage($input: UpdateImageInput!) {
    updateImage(input: $input) {
      id
      title
      description
      tags {
        id
        name
      }
      collectionId
      aiMeta
      printMeta
      height
      width
      dimensionType
      isPublic
      artworkSpecs {
        mediumType
        camera
        lens
        focalLength
        aperture
        shutterSpeed
        iso
        medium
        technique
        subMedium
        software
        mediumCategory {
          id
        }
        hidden
      }
    }
  }
`;

/**
 * Selection-based bulk actions (ImageSelection: ids, folders, or a select-all filter pinned
 * to asOf, minus exceptIds). Each is one request returning only a count; at most 10,000.
 */
export const UPDATE_IMAGES_MUTATION = gql`
  mutation UpdateImages($selection: ImageSelection!, $updates: BulkUpdateImageInput!) {
    updateImages(selection: $selection, updates: $updates) {
      count
    }
  }
`;

export const REMOVE_IMAGES_MUTATION = gql`
  mutation RemoveImages($selection: ImageSelection!) {
    removeImages(selection: $selection) {
      count
    }
  }
`;

/** Sends an upload whose processing failed back to the processor (no-op unless failed). */
export const RETRY_IMAGE_MUTATION = gql`
  mutation RetryImage($id: ID!) {
    retryImage(id: $id) {
      id
      status
    }
  }
`;

export const MOVE_IMAGES_MUTATION = gql`
  mutation MoveImages($selection: ImageSelection!, $collectionId: ID) {
    moveImages(selection: $selection, collectionId: $collectionId) {
      count
    }
  }
`;

export const CREATE_COMPLETION = gql`
  mutation CreateCompletion($input: CreateCompletionInput!) {
    createCompletion(input: $input) {
      id
      response
      responseHtml
    }
  }
`;

export const UPDATE_RATING = gql`
  mutation UpdateRating($id: ID!, $rating: Int) {
    updateRating(id: $id, rating: $rating) {
      id
      rating
    }
  }
`;

export const GENERATE_CONTENT = gql`
  mutation GenerateContent($input: GenerateContentInput!) {
    generateContent(input: $input) {
      content
      jsonArray
    }
  }
`;

export const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken)
  }
`;

export const UPSCALE_MUTATION = gql`
  mutation upscaleImage($input: UpscaleImageInput!) {
    upscaleImage(input: $input) {
      id
      status
    }
  }
`;

export const CREATE_USER_COMPLETION_TYPE_RULE = gql`
  mutation CreateUserCompletionTypeRule($input: CreateUserCompletionTypeRuleInput!) {
    createUserCompletionTypeRule(input: $input) {
      id
      active
      rules
    }
  }
`;

export const UPDATE_USER_COMPLETION_TYPE_RULE = gql`
  mutation UpdateUserCompletionTypeRule($input: UpdateUserCompletionTypeRuleInput!) {
    updateUserCompletionTypeRule(input: $input) {
      id
      active
      rules
    }
  }
`;

export const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($email: String!) {
    forgotPassword(email: $email)
  }
`;

export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($reset_token: String!, $password: String!) {
    resetPassword(reset_token: $reset_token, password: $password)
  }
`;

export const UPDATE_COMPLETION = gql`
  mutation UpdateCompletion($input: UpdateCompletionInput!) {
    updateCompletion(input: $input) {
      id
      responseHtml
    }
  }
`;

export const CREATE_SAVE = gql`
  mutation CreateSave($input: CreateSaveInput!) {
    createSave(input: $input) {
      id
    }
  }
`;

export const CREATE_MOCK_SAVE = gql`
  mutation CreateMockSave($input: CreateMockSaveInput!) {
    createMockSave(input: $input) {
      id
      imageId
      mockId
    }
  }
`;

export const DELETE_MOCK_SAVE = gql`
  mutation DeleteMockSave($input: DeleteMockSaveInput!) {
    deleteMockSave(input: $input)
  }
`;

export const CREATE_LABEL = gql`
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) {
      id
      name
    }
  }
`;

export const DELETE_SAVE = gql`
  mutation DeleteSave($id: ID!) {
    deleteSave(id: $id) {
      id
    }
  }
`;

export const UPDATE_SAVE = gql`
  mutation UpdateSave($input: UpdateSaveInput!) {
    updateSave(input: $input) {
      id
    }
  }
`;

export const TEST_COMPLETION = gql`
  mutation TestCompletion($input: TestCompletionInput!) {
    testCompletion(input: $input) {
      response
      responseHtml
      inference
    }
  }
`;

export const CONNECT_INSTAGRAM_USER_INTEGRATION = gql`
  mutation ConnectInstagramUserIntegration($input: ConnectInstagramUserIntegrationInput!) {
    connectInstagramUserIntegration(input: $input) {
      id
    }
  }
`;

export const DELETE_USER_INTEGRATION = gql`
  mutation DeleteUserIntegration($input: DeleteUserIntegrationInput!) {
    deleteUserIntegration(input: $input)
  }
`;

export const UPDATE_USER_INTEGRATION = gql`
  mutation UpdateUserIntegration($input: UpdateUserIntegrationInput!) {
    updateUserIntegration(input: $input)
  }
`;

export const CREATE_SOCIAL_POST = gql`
  mutation CreateSocialPost($input: CreateSocialPostInput!) {
    createSocialPost(input: $input) {
      id
    }
  }
`;

export const GENERATE_POST_SIGNED_URL = gql`
  mutation generatePostSignedUrl($input: PostSignedUrlInput!) {
    generatePostSignedUrl(input: $input)
  }
`;

export const TOOGLE_FAVORITE_COMPLETION_TYPE = gql`
  mutation ToggleFavoriteCompletionType($completionTypeId: String!, $isFavorited: Boolean!) {
    toggleFavoriteCompletionType(completionTypeId: $completionTypeId, isFavorited: $isFavorited)
  }
`;

export const TRACK_COMPLETION_TYPE_USAGE = gql`
  mutation TrackCompletionTypeUsage($completionTypeId: String!) {
    trackCompletionTypeUsage(completionTypeId: $completionTypeId)
  }
`;

export const DELETE_SOCIAL_POST = gql`
  mutation DeleteSocialPost($id: ID!) {
    deleteSocialPost(id: $id)
  }
`;

export const UPDATE_SOCIAL_POST = gql`
  mutation UpdateSocialPost($input: UpdateSocialPostInput!) {
    updateSocialPost(input: $input) {
      id
      content
      mediaUrls
      scheduledFor
      status
    }
  }
`;

export const UPDATE_ONBOARDING = gql`
  mutation UpdateOnboarding($input: OnboardingInput!) {
    updateOnboarding(input: $input) {
      id
      userId
      firstName
      lastName
      email
      city
      state
      zipCode
      street
      street2
      country
      business
      about
      logoUrl
      websiteName
      subdomain
      themeId
      resellerId
      planName
      planStatus
      type
      completedSteps
      currentStep
      isComplete
      createdAt
      updatedAt
    }
  }
`;

export const COMPLETE_ONBOARDING_STEP = gql`
  mutation CompleteOnboardingStep($step: String!) {
    completeOnboardingStep(step: $step) {
      id
      userId
      completedSteps
      currentStep
      isComplete
    }
  }
`;

export const CREATE_IMAGE_MUTATION = gql`
  mutation CreateImage($input: CreateImageInput!) {
    createImage(input: $input) {
      id
      originalUrl
      originalWidth
      originalHeight
      status
      fileMeta
      isFirstUpload
      isTier2Activated
    }
  }
`;

export const CREATE_COLLECTION = gql`
  mutation CreateCollection($input: CreateCollectionInput!) {
    createCollection(input: $input) {
      id
      name
      path
      description
      ephemeral
      createdAt
      updatedAt
      imageCount(nested: true)
      nestedImages(limit: 6) {
        id
        title
        thumbnailUrl
        optimizedUrl
        width
        height
        dimensionType
        isPublic
      }
    }
  }
`;

export const UPDATE_COLLECTION = gql`
  mutation UpdateCollection($input: UpdateCollectionInput!) {
    updateCollection(input: $input) {
      id
      name
      path
      description
      ephemeral
      createdAt
      updatedAt
      imageCount(nested: true)
      nestedImages(limit: 6) {
        id
        title
        thumbnailUrl
        optimizedUrl
        width
        height
        dimensionType
        isPublic
      }
    }
  }
`;

export const DELETE_COLLECTION = gql`
  mutation DeleteCollection($input: DeleteCollectionInput!) {
    deleteCollection(input: $input) {
      id
    }
  }
`;

export const GENERATE_SIGNED_URL = gql`
  mutation GenerateSignedUrl($input: SignedUrlInput!) {
    generateSignedUrl(input: $input)
  }
`;

export const CREATE_ASF_WEBSITE = gql`
  mutation CreateAsfWebsite {
    createAsfWebsite
  }
`;

export const CREATE_SERIES = gql`
  mutation CreateSeries($input: CreateSeriesInput!) {
    createSeries(input: $input) {
      id
      name
      description
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_SERIES = gql`
  mutation UpdateSeries($input: UpdateSeriesInput!) {
    updateSeries(input: $input) {
      id
      name
      description
    }
  }
`;

export const DELETE_SERIES = gql`
  mutation DeleteSeries($input: DeleteSeriesInput!) {
    deleteSeries(input: $input) {
      id
    }
  }
`;

export const ADD_IMAGE_TO_SERIES = gql`
  mutation AddImageToSeries($input: SeriesAddImageInput!) {
    addImageToSeries(input: $input) {
      id
      name
      description
    }
  }
`;

export const REMOVE_IMAGE_FROM_SERIES = gql`
  mutation RemoveImageFromSeries($input: SeriesRemoveImageInput!) {
    removeImageFromSeries(input: $input) {
      id
      name
      description
    }
  }
`;

export const UPDATE_MOCK_MUTATION = gql`
  mutation UpdateMock($input: UpdateMockInput!) {
    updateMock(input: $input) {
      id
      uuid
      description
      scaleFactor
      maxWidth
      maxHeight
      enabled
      tags
      palette
      createdAt
      updatedAt
      deletedAt
    }
  }
`;

export const DELETE_MOCK_MUTATION = gql`
  mutation DeleteMock($input: DeleteMockInput!) {
    deleteMock(input: $input) {
      id
      uuid
      description
      scaleFactor
      maxWidth
      maxHeight
      enabled
      tags
      palette
      createdAt
      updatedAt
      deletedAt
    }
  }
`;

export const RESTORE_MOCK_MUTATION = gql`
  mutation RestoreMock($input: RestoreMockInput!) {
    restoreMock(input: $input) {
      id
      uuid
      description
      scaleFactor
      maxWidth
      maxHeight
      enabled
      tags
      palette
      createdAt
      updatedAt
      deletedAt
    }
  }
`;

export const CREATE_CUSTOMER_SESSION = gql`
  mutation CreateCustomerSession {
    createCustomerSession {
      clientSecret
    }
  }
`;

export const CREATE_BILLING_PORTAL_SESSION = gql`
  mutation CreateBillingPortalSession {
    createBillingPortalSession {
      url
    }
  }
`;

export const CREATE_SUBSCRIPTION_BILLING_PORTAL_SESSION = gql`
  mutation CreateSubscriptionBillingPortalSession {
    createSubscriptionBillingPortalSession {
      url
    }
  }
`;

export const CREATE_AUTOPILOT_CUSTOMER_SESSION = gql`
  mutation CreateAutopilotCustomerSession {
    createAutopilotCustomerSession {
      clientSecret
    }
  }
`;

export const CREATE_AUTOPILOT_BILLING_PORTAL_SESSION = gql`
  mutation CreateAutopilotBillingPortalSession {
    createAutopilotBillingPortalSession {
      url
    }
  }
`;

export const CREATE_HUMAN_MADE_ART_BILLING_PORTAL_SESSION = gql`
  mutation CreateHumanMadeArtBillingPortalSession {
    createHumanMadeArtBillingPortalSession {
      url
    }
  }
`;

export const CREATE_CHAT_WITH_MESSAGE = gql`
  mutation CreateChatWithMessage($input: CreateChatWithMessageInput!, $last: Int = ${CHAT_PARTS_DEFAULT}) {
    createChatWithMessage(input: $input) {
      chat {
        id
        title
        type
        targetId
        targetType
        parts(last: $last) {
          id
          role
          partIndex
          type
          content
          toolCall
          attachment
          reasoning
          usage
          metadata
          createdAt
        }
        createdAt
        updatedAt
      }
      chatPart {
        id
        role
        type
        content
        createdAt
      }
    }
  }
`;

export const UPDATE_CHAT_TITLE = gql`
  mutation UpdateChat($input: UpdateChatInput!) {
    updateChat(input: $input) {
      id
      title
      updatedAt
    }
  }
`;

export const UPDATE_CHAT_SHARED = gql`
  mutation UpdateChatShared($input: ChatSharedInput!) {
    updateChatShared(input: $input) {
      id
      shared
      updatedAt
    }
  }
`;

export const DELETE_CHAT = gql`
  mutation DeleteChat($input: DeleteChatInput!) {
    deleteChat(input: $input) {
      id
      title
    }
  }
`;

// Legacy aliases for backwards compatibility
export const CREATE_CONVERSATION_WITH_MESSAGE = CREATE_CHAT_WITH_MESSAGE;
export const UPDATE_CONVERSATION_TITLE = UPDATE_CHAT_TITLE;
export const DELETE_CONVERSATION = DELETE_CHAT;

export const CREATE_MOCK_FILTER = gql`
  mutation CreateMockFilter($input: CreateMockFilterInput!) {
    createMockFilter(input: $input) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_MOCK_FILTER = gql`
  mutation UpdateMockFilter($input: UpdateMockFilterInput!) {
    updateMockFilter(input: $input) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_MOCK_FILTER = gql`
  mutation DeleteMockFilter($input: DeleteMockFilterInput!) {
    deleteMockFilter(input: $input) {
      id
      name
      createdAt
      updatedAt
    }
  }
`;

export const ASSIGN_MOCK_FILTER = gql`
  mutation AssignMockFilter($input: AssignMockFilterInput!) {
    assignMockFilter(input: $input) {
      id
      name
    }
  }
`;

export const UNASSIGN_MOCK_FILTER = gql`
  mutation UnassignMockFilter($input: UnassignMockFilterInput!) {
    unassignMockFilter(input: $input) {
      id
      name
    }
  }
`;
export const UPDATE_CHAT_PART_EVALUATION_MUTATION = gql`
  mutation UpdateChatPartEvaluation($input: ChatPartEvaluationInput!) {
    updateChatPartEvaluation(input: $input) {
      id
      good
      notes
      updatedAt
    }
  }
`;

export const DELETE_MEMORY_MUTATION = gql`
  mutation DeleteMemory($id: ID!) {
    deleteMemory(id: $id)
  }
`;

export const DELETE_ALL_MEMORIES_MUTATION = gql`
  mutation DeleteAllMemories {
    deleteAllMemories
  }
`;

// auth.tsx counts uploads for `image_uploaded` analytics from the newest CHAT_PARTS_MAX converted
// parts only (bounded): older uploads in a guest chat past that many parts are not counted.
export const CONVERT_FROM_PUBLIC_MUTATION = gql`
  mutation ConvertFromPublic($input: ConvertFromPublicInput!, $last: Int = ${CHAT_PARTS_MAX}) {
    convertFromPublic(input: $input) {
      id
      title
      type
      targetId
      targetType
      parts(last: $last) {
        id
        role
        partIndex
        type
        content
        toolCall
        attachment
        reasoning
        usage
        metadata
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const ADMIN_UPDATE_PROFILE = gql`
  mutation AdminUpdateProfile($input: UpdateAdminProfileInput!) {
    adminUpdateProfile(input: $input) {
      id
      slug
      isDiscoverable
      isDisabled
      isBanned
      isNsfw
      excludeFromSuggestions
      updatedAt
    }
  }
`;

export const ADMIN_UPDATE_PROFILE_COMPREHENSIVE = gql`
  mutation AdminUpdateProfileComprehensive($input: UpdateAdminProfileComprehensiveInput!) {
    adminUpdateProfileComprehensive(input: $input) {
      id
      userId
      avatarUrl
      bio
      displayName
      artistStatement
      countryCode
      country
      regionCode
      region
      city
      latitude
      longitude
      timezone
      bannerUrl
      slug
      socialLinks
      isPublic
      isDiscoverable
      watermarkEnabled
      watermarkUrl
      watermarkCustomKey
      showMockups
      showArtworkSpecs
      showActivity
      categories {
        id
        name
        slug
      }
      createdAt
      updatedAt
    }
  }
`;

export const UNSUBSCRIBE_FROM_EMAIL_NOTIFICATIONS = gql`
  mutation UnsubscribeFromEmailNotifications($token: String!, $type: String!) {
    unsubscribeFromEmailNotifications(token: $token, type: $type)
  }
`;

export const MUTE_DIGEST_FROM_TOKEN = gql`
  mutation MuteDigestFromToken($token: String!) {
    muteDigestFromToken(token: $token)
  }
`;

export const UNMUTE_DIGEST_FROM_TOKEN = gql`
  mutation UnmuteDigestFromToken($token: String!) {
    unmuteDigestFromToken(token: $token)
  }
`;

export const ACCEPT_COMMUNITY_INVITE_MUTATION = gql`
  mutation AcceptCommunityInvite($token: String!) {
    acceptCommunityInvite(token: $token) {
      id
      communityId
      role
      status
    }
  }
`;

export const FOLLOW_MUTATION = gql`
  mutation Follow($userId: ID!) {
    follow(userId: $userId) {
      id
      follower {
        asfId
      }
      following {
        asfId
      }
      createdAt
    }
  }
`;

/** A click step opened from a bounty or a series message — records the open, and pays it if it pays. */
export const COMPLETE_BOUNTY_CLICK_MUTATION = gql`
  mutation CompleteBountyClick($bountyId: ID!) {
    completeBountyClick(bountyId: $bountyId)
  }
`;

/** A Connect or Follow made from an onboarding-series artist-card item (#4215). */
export const COMPLETE_SERIES_SUGGESTION_MUTATION = gql`
  mutation CompleteSeriesSuggestion($bountyId: ID!, $artistId: ID!) {
    completeSeriesSuggestion(bountyId: $bountyId, artistId: $artistId)
  }
`;

export const UNFOLLOW_MUTATION = gql`
  mutation Unfollow($userId: ID!) {
    unfollow(userId: $userId)
  }
`;

export const ASSIGN_ONBOARDING_FLOW = gql`
  mutation AssignOnboardingFlow {
    assignOnboardingFlow {
      id
      currentStepCode
      completedRequiredCount
      completedTotalCount
      outcome
    }
  }
`;

export const RECORD_ONBOARDING_STEP_EVENT = gql`
  mutation RecordOnboardingStepEvent($input: RecordOnboardingStepEventInput!) {
    recordOnboardingStepEvent(input: $input) {
      id
      currentStepCode
      completedRequiredCount
      completedTotalCount
      outcome
    }
  }
`;

export const JOIN_COMMUNITY = gql`
  mutation JoinCommunity($input: JoinCommunityInput!) {
    joinCommunity(input: $input) {
      id
      role
      status
    }
  }
`;

export const LEAVE_COMMUNITY = gql`
  mutation LeaveCommunity($input: LeaveCommunityInput!) {
    leaveCommunity(input: $input)
  }
`;

export const COPY_IMAGE_TO_SOCIAL = gql`
  mutation CopyImageToSocial($input: CopyImageToSocialInput!) {
    copyImageToSocial(input: $input) {
      id
      status
      optimizedUrl
      thumbnailUrl
    }
  }
`;

export const CREATE_COMMUNITY_POST = gql`
  ${POST_AUTHOR_FIELDS}

  mutation CreateCommunityPost($input: CreateCommunityPostInput!) {
    createCommunityPost(input: $input) {
      id
      slug
      title
      type
      content
      status
      voteScore
      commentCount
      createdAt
      author {
        ...PostAuthorFields
      }
    }
  }
`;

export const TOGGLE_FAVORITE_COMPLETION_TYPE = gql`
  mutation ToggleFavoriteCompletionType($completionTypeId: ID!, $isFavorited: Boolean!) {
    toggleFavoriteCompletionType(completionTypeId: $completionTypeId, isFavorited: $isFavorited)
  }
`;

export const CREATE_ONBOARDING_VERSION = gql`
  mutation CreateOnboardingVersion($input: CreateOnboardingVersionInput!) {
    createOnboardingVersion(input: $input) {
      id
      version
      name
      description
      isDefault
      totalStepCount
      requiredStepCount
      createdAt
    }
  }
`;

export const SET_DEFAULT_ONBOARDING_VERSION = gql`
  mutation SetDefaultOnboardingVersion($id: ID!) {
    setDefaultVersion(id: $id) {
      id
      version
      isDefault
    }
  }
`;

export const REPORT_CONTENT_MUTATION = gql`
  mutation ReportContent($contentType: String!, $contentId: ID!, $category: ReportCategory!, $description: String) {
    reportContent(contentType: $contentType, contentId: $contentId, category: $category, description: $description) {
      id
      contentType
      contentId
      category
      createdAt
    }
  }
`;

export const REQUEST_CONTENT_REVIEW_MUTATION = gql`
  mutation RequestContentReview($message: String) {
    requestContentReview(message: $message) {
      id
      userId
      message
      createdAt
    }
  }
`;

export const COMPLETE_CONTENT_REVIEW_REQUEST_MUTATION = gql`
  mutation CompleteContentReviewRequest($id: ID!, $message: String) {
    completeContentReviewRequest(id: $id, message: $message)
  }
`;

export const RESOLVE_SCAN_MUTATION = gql`
  mutation ResolveScan($input: ResolveScanInput!) {
    resolveScan(input: $input) {
      id
      resolution
    }
  }
`;

export const UPSERT_FEATURE_WIZARD = gql`
  mutation UpsertFeatureWizard($input: UpsertFeatureWizardInput!) {
    upsertFeatureWizard(input: $input) {
      id
      slug
      name
      description
      status
      publishedAt
      controlPercent
      targetEventName
      handoffHref
      steps {
        id
        stepCode
        position
        config
      }
    }
  }
`;

export const SET_FEATURE_WIZARD_STATUS = gql`
  mutation SetFeatureWizardStatus($id: ID!, $status: FeatureWizardStatus!) {
    setFeatureWizardStatus(id: $id, status: $status) {
      id
      slug
      status
      publishedAt
    }
  }
`;

/**
 * Every transition of a viewer's wizard state. Replay-safe by design, so a retry after a
 * failed call costs nothing and cannot duplicate the row or change its audience.
 */
export const RECORD_FEATURE_WIZARD_EVENT = gql`
  mutation RecordFeatureWizardEvent($input: RecordFeatureWizardEventInput!) {
    recordFeatureWizardEvent(input: $input) {
      id
      assignment
      outcome
    }
  }
`;

export const DELETE_EXTERNAL_SAVE = gql`
  mutation DeleteExternalSave($input: ExternalSaveKeyInput!) {
    deleteExternalSave(input: $input)
  }
`;

/**
 * Artwork engagement from inside the notifications modal.
 *
 * `like`/`unlike` rather than `likeImage`: the counter mutation is anonymous, writes no
 * per-user row, and cannot be undone, so a reader could never see whether they had
 * already reacted. `EngagementTargetType` has always included `IMAGE` — artwork simply
 * never used it.
 */
export const LIKE_ARTWORK_MUTATION = gql`
  mutation LikeArtwork($targetId: ID!) {
    like(targetType: IMAGE, targetId: $targetId) {
      id
      targetId
    }
  }
`;

export const UNLIKE_ARTWORK_MUTATION = gql`
  mutation UnlikeArtwork($targetId: ID!) {
    unlike(targetType: IMAGE, targetId: $targetId)
  }
`;

export const CREATE_IMAGE_COMMENT_MUTATION = gql`
  mutation CreateImageComment($imageId: ID!, $content: String!) {
    createImageComment(imageId: $imageId, content: $content) {
      id
      imageId
      content
      createdAt
    }
  }
`;

export const CREATE_COMMUNITY_COMMENT_MUTATION = gql`
  ${POST_AUTHOR_FIELDS}

  mutation CreateCommunityComment($input: CreateCommunityCommentInput!) {
    createCommunityComment(input: $input) {
      id
      content
      createdAt
      voteScore
      author {
        ...PostAuthorFields
      }
    }
  }
`;

/**
 * Up/down on a community post.
 *
 * `voteContent` is a three-branch machine on the server — casting, changing and
 * clearing a vote all arrive as the same mutation — so the client sends the direction
 * it wants and reads the post's new score back rather than computing one.
 */
export const VOTE_POLL_MUTATION = gql`
  mutation VotePoll($input: VotePollInput!) {
    votePoll(input: $input) {
      poll {
        id
        totalVoteCount
        currentUserOptionId
        options {
          id
          voteCount
        }
      }
    }
  }
`;

export const CREATE_CRITIQUE_MUTATION = gql`
  ${POST_AUTHOR_FIELDS}

  mutation CreateCritique($input: CreateCritiqueInput!) {
    createCritique(input: $input) {
      id
      content
      createdAt
      voteScore
      author {
        ...PostAuthorFields
      }
      critique {
        id
      }
    }
  }
`;

export const SUBMIT_PRICING_FEEDBACK_MUTATION = gql`
  mutation SubmitPricingFeedback($input: SubmitPricingFeedbackInput!) {
    submitPricingFeedback(input: $input) {
      pricingRequest {
        id
        currentUserValueCents
        canSeeResults
        results {
          submissionCount
          averageCents
          medianCents
          lowCents
          highCents
        }
      }
    }
  }
`;

export const SUBMIT_ART_RATING_MUTATION = gql`
  mutation SubmitArtRating($input: SubmitArtRatingInput!) {
    submitArtRating(input: $input) {
      ratingRequest {
        id
        currentUserRating
        canSeeResults
        results {
          submissionCount
          average
          median
        }
      }
    }
  }
`;

export const VOTE_CONTENT_MUTATION = gql`
  mutation VoteContent($input: VoteContentInput!) {
    voteContent(input: $input) {
      vote {
        id
        direction
      }
      post {
        id
        voteScore
        # Selected so the cache carries the viewer's new state too. Without it the
        # normalized post keeps its stale currentUserVote and the pressed control has to
        # be tracked by hand for the life of the modal.
        currentUserVote {
          id
          direction
        }
      }
    }
  }
`;

export const TOGGLE_REACTION_MUTATION = gql`
  mutation ToggleReaction($targetType: ReactionTargetType!, $targetId: ID!, $emoji: String!) {
    toggleReaction(targetType: $targetType, targetId: $targetId, emoji: $emoji) {
      isReacted
      responderCount
      reactions {
        emoji
        count
        hasViewerReacted
      }
    }
  }
`;

/**
 * A social image — the storage artwork does NOT live in.
 *
 * A work in progress shared to answer "whatcha working on?" is a post attachment, not a
 * portfolio piece: it must not appear in My Artwork, and this is the path that keeps it
 * out.
 */
export const CREATE_SOCIAL_IMAGE_MUTATION = gql`
  mutation CreateSocialImage($input: CreateSocialImageInput!) {
    createSocialImage(input: $input) {
      id
      fileMeta
    }
  }
`;

export const PROCESS_SOCIAL_IMAGE_MUTATION = gql`
  mutation ProcessSocialImage($input: ProcessSocialImageInput!) {
    processSocialImage(input: $input) {
      id
    }
  }
`;

export const CREATE_COMMUNITY_POST_MUTATION = gql`
  mutation CreateCommunityPost($input: CreateCommunityPostInput!) {
    createCommunityPost(input: $input) {
      id
      slug
      title
      community {
        slug
      }
    }
  }
`;
